import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  agentService,
  GetAgentsParams,
  CreateAgentRequest,
  UpdateAgentRequest,
} from "../api/services/agents/agentService";

const AGENTS_STALE_TIME =
  Number(process.env.REACT_APP_AGENTS_STALE_TIME) || 5 * 60 * 1000;

export const agentKeys = {
  all: ["agents"] as const,
  lists: () => [...agentKeys.all, "list"] as const,
  list: (params: GetAgentsParams) => [...agentKeys.lists(), params] as const,
  details: () => [...agentKeys.all, "detail"] as const,
  detail: (id: string) => [...agentKeys.details(), id] as const,
};

export const useAgents = (params: GetAgentsParams = {}) => {
  return useQuery({
    queryKey: agentKeys.list(params),
    queryFn: () => agentService.getAgents(params),
    staleTime: AGENTS_STALE_TIME,
  });
};

export const useAgent = (id: string) => {
  return useQuery({
    queryKey: agentKeys.detail(id),
    queryFn: () => agentService.getAgent(id),
    enabled: !!id,
    staleTime: AGENTS_STALE_TIME,
  });
};

export const useCreateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgentRequest) => agentService.createAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
    },
  });
};

export const useSearchAgents = (query: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["agents", "search", query],
    queryFn: () => agentService.searchAgents(query),
    enabled: enabled && !!query.trim(),
    staleTime: AGENTS_STALE_TIME,
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentId,
      data,
    }: {
      agentId: string;
      data: UpdateAgentRequest;
    }) => agentService.updateAgent(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentService.deleteAgent(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: agentKeys.detail(deletedId) });

      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
    },
  });
};

export const useAgentManagement = (initialParams: GetAgentsParams = {}) => {
  const queryClient = useQueryClient();

  const agentsQuery = useAgents(initialParams);
  const createMutation = useCreateAgent();
  const updateMutation = useUpdateAgent();
  const deleteMutation = useDeleteAgent();

  const refreshAgents = () => {
    queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
  };

  const clearCache = () => {
    queryClient.clear();
  };

  return {
    agents: agentsQuery.data || {
      items: [],
      pagination: { total: 0, skip: 0, limit: 0, returned: 0 },
      filtersApplied: {
        name: null,
        email: null,
        phone: null,
        agentId: null,
      },
    },
    isLoading: agentsQuery.isLoading,
    isError: agentsQuery.isError,
    error: agentsQuery.error,

    createAgent: createMutation.mutate,
    updateAgent: updateMutation.mutate,
    deleteAgent: deleteMutation.mutate,

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    refreshAgents,
    clearCache,
  };
};
